import { makeAutoObservable, runInAction } from 'mobx'
import { logger } from 'shared/lib'
import { toastStore } from 'shared/ui'
import {
  type Integration,
  IntegrationKey,
  IntegrationsApi,
  type IResponseGoogleCalendarListItem,
  type IResponseIntegrationStateAccount,
} from 'entities/Integrations'
import { buildGoogleCalendarMergeFields } from 'entities/MergeField'
import type { IEntitiesStore } from 'entities/EntitiesExport'
import { ManageIntegrationConnectionStore } from 'features/ManageIntegrationConnection'

type ICalendarsByAccount = {
  items: IResponseGoogleCalendarListItem[]
  loading: boolean
}

export class GoogleCalendarIntegrationStore {
  private _accounts: IResponseIntegrationStateAccount[] = []
  private _calendarsByAccount = new Map<string, ICalendarsByAccount>()
  private _search = ''
  private _loading = false
  private _connecting = false
  private _integration: Integration | null = null
  private readonly _manageIntegrationConnectionStore = new ManageIntegrationConnectionStore()

  constructor(private _entities: IEntitiesStore) {
    makeAutoObservable(this)
  }

  get accounts() {
    return this._accounts
  }

  get loading() {
    return this._loading
  }

  get isGoogleAccountConnecting() {
    return this._connecting
  }

  get search() {
    return this._search
  }

  get hasAccounts() {
    return this._accounts.length > 0
  }

  setSearch = (value: string) => {
    this._search = value
  }

  calendarsForAccount = (accountId: string): IResponseGoogleCalendarListItem[] => {
    return this._calendarsByAccount.get(accountId)?.items ?? []
  }

  init = async (integration: Integration) => {
    this._integration = integration
    this._entities.mergeFieldStore.addMergeFieldsIntegrations(
      IntegrationKey.google_calendar,
      buildGoogleCalendarMergeFields(integration.id)
    )
    await this.fetchAccounts()
  }

  fetchAccounts = async () => {
    if (!this._integration) return
    try {
      runInAction(() => {
        this._loading = true
      })
      const { data } = await IntegrationsApi.getIntegrationCalendarState(
        IntegrationKey.google_calendar
      )
      runInAction(() => {
        this._accounts = data.accounts ?? []
      })
      await Promise.all(this._accounts.map((account) => this.fetchCalendars(account)))
    } catch (e) {
      logger.error(e)
    } finally {
      runInAction(() => {
        this._loading = false
      })
    }
  }

  fetchCalendars = async (account: IResponseIntegrationStateAccount) => {
    const accountId = account.external_user_id
    runInAction(() => {
      this._calendarsByAccount.set(accountId, {
        items: this._calendarsByAccount.get(accountId)?.items ?? [],
        loading: true,
      })
    })
    try {
      const { data } = await IntegrationsApi.getGoogleCalendarList(IntegrationKey.google_calendar, {
        account_id: accountId,
      })
      runInAction(() => {
        this._calendarsByAccount.set(accountId, {
          items: data.items,
          loading: false,
        })
      })
    } catch (e) {
      logger.error(e)
      runInAction(() => {
        this._calendarsByAccount.set(accountId, {
          items: this._calendarsByAccount.get(accountId)?.items ?? [],
          loading: false,
        })
      })
    }
  }

  connectAccount = async () => {
    if (this._connecting || !this._integration) return
    runInAction(() => {
      this._connecting = true
    })
    try {
      const isConnected = await this._manageIntegrationConnectionStore.connect(this._integration)
      if (isConnected) {
        await this.fetchAccounts()
        toastStore.add({ type: 'success', title: 'Google account connected' })
      }
    } finally {
      runInAction(() => {
        this._connecting = false
      })
    }
  }

  dispose = () => {
    this._entities.mergeFieldStore.removeDynamicMergeFields(IntegrationKey.google_calendar)
    this._accounts = []
    this._calendarsByAccount.clear()
    this._search = ''
    this._integration = null
  }
}
